/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../chunks/tslib.es6.js";
import { ensureType as t } from "../symbols.js";
import s from "../core/Error.js";
import { clone as i } from "../core/lang.js";
import o from "../core/Logger.js";
import { deepMerge as l } from "../core/object.js";
import { watch as r } from "../core/reactiveUtils.js";
import { property as u } from "../core/accessorSupport/decorators/property.js";
import { cast as n } from "../core/accessorSupport/decorators/cast.js";
import { enumeration as a } from "../core/accessorSupport/decorators/enumeration.js";
import { reader as p } from "../core/accessorSupport/decorators/reader.js";
import { subclass as f } from "../core/accessorSupport/decorators/subclass.js";
import { writer as d } from "../core/accessorSupport/decorators/writer.js";
import { diff as c } from "../core/accessorSupport/diffUtils.js";
import { ensureType as h, ensureString as m } from "../core/accessorSupport/ensureType.js";
import { collectArcadeFieldNames as y, collectField as b } from "../layers/support/fieldUtils.js";
import v from "../portal/Portal.js";
import _ from "./Renderer.js";
import { VisualVariablesMixin as V } from "./mixins/VisualVariablesMixin.js";
import { rendererSymbolProperty as q, rendererBackgroundFillSymbolProperty as g } from "./support/commonProperties.js";
import { LegendOptions as I } from "./support/LegendOptions.js";
import S from "./support/UniqueValue.js";
import w from "./support/UniqueValueClass.js";
import U from "./support/UniqueValueGroup.js";
import j from "./support/UniqueValueInfo.js";
import { loadArcade as F } from "../support/arcadeOnDemand.js";
import { f as O, t as G } from "../chunks/persistableUrlUtils.js";
import { fetchStyle as D } from "../symbols/support/styleUtils.js";
import x from "../symbols/WebStyleSymbol.js";
var E;
const M = "esri.renderers.UniqueValueRenderer",
  N = o.getLogger(M),
  R = "uvInfos-watcher",
  P = "uvGroups-watcher",
  C = ",",
  $ = h(j);
function z(e) {
  const {
      field1: t,
      field2: s,
      field3: i,
      fieldDelimiter: o,
      uniqueValueInfos: l,
      valueExpression: r
    } = e,
    u = !(!t || !s);
  return [{
    classes: (l ?? []).map(e => {
      const {
          symbol: l,
          label: n,
          value: a,
          description: p
        } = e,
        [f, d, c] = u ? a?.toString()?.split(o || "") || [] : [a],
        h = [];
      return (t || r) && h.push(f), s && h.push(d), i && h.push(c), {
        symbol: l,
        label: n,
        values: [h],
        description: p
      };
    })
  }];
}
let k = E = class extends V(_) {
  constructor(e) {
    super(e), this._valueInfoMap = {}, this._isDefaultSymbolDerived = !1, this._isInfosSource = null, this.type = "unique-value", this.backgroundFillSymbol = null, this.orderByClassesEnabled = !1, this.valueExpressionTitle = null, this.legendOptions = null, this.defaultLabel = null, this.portal = null, this.styleOrigin = null, this.diff = {
      uniqueValueInfos(e, t) {
        if (!e && !t) return;
        if (!e || !t) return {
          type: "complete",
          oldValue: e,
          newValue: t
        };
        let s = !1;
        const i = {
          type: "collection",
          added: [],
          removed: [],
          changed: [],
          unchanged: []
        };
        for (let o = 0; o < t.length; o++) {
          const l = e.find(e => e.value === t[o].value);
          l ? c(l, t[o]) ? (i.changed.push({
            type: "complete",
            oldValue: l,
            newValue: t[o]
          }), s = !0) : i.unchanged.push({
            oldValue: l,
            newValue: t[o]
          }) : (i.added.push(t[o]), s = !0);
        }
        for (let o = 0; o < e.length; o++) {
          t.find(t => t.value === e[o].value) || (i.removed.push(e[o]), s = !0);
        }
        return s ? i : void 0;
      }
    }, this._set("uniqueValueInfos", []), this._set("uniqueValueGroups", []);
  }
  get _cache() {
    return {
      compiledFunc: null
    };
  }
  set field(e) {
    this._set("field", e), this._updateFieldDelimiter(), this._updateUniqueValues();
  }
  castField(e) {
    return null == e || "function" == typeof e ? e : m(e);
  }
  writeField(e, t, i, o) {
    "string" == typeof e ? t[i] = e : o?.messages ? o.messages.push(new s("property:unsupported", "UniqueValueRenderer.field set to a function cannot be written to JSON")) : N.error(".field: cannot write field to JSON since it's not a string value");
  }
  set field2(e) {
    this._set("field2", e), this._updateFieldDelimiter(), this._updateUniqueValues();
  }
  set field3(e) {
    this._set("field3", e), this._updateUniqueValues();
  }
  set valueExpression(e) {
    this._set("valueExpression", e), this._updateUniqueValues();
  }
  set defaultSymbol(e) {
    this._isDefaultSymbolDerived = !1, this._set("defaultSymbol", e);
  }
  set fieldDelimiter(e) {
    this._set("fieldDelimiter", e), this._updateUniqueValues();
  }
  readPortal(e, t, s) {
    return s.portal || v.getDefault();
  }
  readStyleOrigin(e, t, s) {
    if (t.styleName) return Object.freeze({
      styleName: t.styleName
    });
    if (t.styleUrl) {
      const e = O(t.styleUrl, s);
      return Object.freeze({
        styleUrl: e
      });
    }
  }
  writeStyleOrigin(e, t, s, i) {
    e.styleName ? t.styleName = e.styleName : e.styleUrl && (t.styleUrl = G(e.styleUrl, i));
  }
  set uniqueValueGroups(e) {
    this.styleOrigin ? N.error("#uniqueValueGroups=", "Cannot modify unique value groups of a UniqueValueRenderer created from a web style") : (this._set("uniqueValueGroups", e), this._updateInfosFromGroups(), this._isInfosSource = !1, this._watchUniqueValueGroups());
  }
  set uniqueValueInfos(e) {
    this.styleOrigin ? N.error("#uniqueValueInfos=", "Cannot modify unique value infos of a UniqueValueRenderer created from a web style") : (this._set("uniqueValueInfos", e), this._updateValueInfoMap(), this._updateGroupsFromInfos(), this._isInfosSource = !0, this._watchUniqueValueInfos());
  }
  addUniqueValueInfo(e, s) {
    if (this.styleOrigin) return void N.error("#addUniqueValueInfo()", "Cannot modify unique value infos of a UniqueValueRenderer created from a web style");
    let i;
    i = "object" == typeof e ? $(e) : new j({
      value: e,
      symbol: t(s)
    }), this.uniqueValueInfos?.push(i), this._valueInfoMap[i.value] = i, this._updateGroupsFromInfos(), this._isInfosSource = !0, this._watchUniqueValueInfos();
  }
  removeUniqueValueInfo(e) {
    if (this.styleOrigin) return void N.error("#removeUniqueValueInfo()", "Cannot modify unique value infos of a UniqueValueRenderer created from a web style");
    const t = this.uniqueValueInfos;
    if (t) for (let s = 0; s < t.length; s++) {
      const i = t[s];
      if (String(i.value) === String(e)) {
        delete this._valueInfoMap[e], t.splice(s, 1);
        break;
      }
    }
    this._updateGroupsFromInfos(), this._isInfosSource = !0, this._watchUniqueValueInfos();
  }
  async getUniqueValueInfo(e, t) {
    let s = t;
    return this.valueExpression && null == t?.arcade && (s = {
      ...s,
      arcade: await F()
    }), this._getUniqueValueInfo(e, s);
  }
  getSymbol(e, t) {
    if (this.valueExpression && null == t?.arcade) return void N.error("#getSymbol()", "Please use getSymbolAsync if valueExpression is used");
    const s = this._getUniqueValueInfo(e, t);
    return s?.symbol || this.defaultSymbol;
  }
  async getSymbolAsync(e, t) {
    let s = t;
    if (this.valueExpression && null == s?.arcade) {
      const e = await F(),
        {
          arcadeUtils: t
        } = e;
      t.hasGeometryOperations(this.valueExpression) && (await t.enableGeometryOperations()), s = {
        ...s,
        arcade: e
      };
    }
    const i = this._getUniqueValueInfo(e, s);
    return i?.symbol || this.defaultSymbol;
  }
  getSymbols() {
    const e = [];
    for (const t of this.uniqueValueInfos ?? []) t.symbol && e.push(t.symbol);
    return this.defaultSymbol && e.push(this.defaultSymbol), e;
  }
  getAttributeHash() {
    return this.visualVariables && this.visualVariables.reduce((e, t) => e + t.getAttributeHash(), "");
  }
  getMeshHash() {
    const e = JSON.stringify(this.backgroundFillSymbol),
      t = JSON.stringify(this.defaultSymbol),
      s = this.uniqueValueInfos?.reduce((e, t) => e + t.getMeshHash(), "");
    return `${e}.${t}.${s}.${`${this.field}.${this.field2}.${this.field3}.${this.fieldDelimiter}`}.${this.valueExpression}`;
  }
  clone() {
    const e = new E({
      field: this.field,
      field2: this.field2,
      field3: this.field3,
      defaultLabel: this.defaultLabel,
      defaultSymbol: i(this.defaultSymbol),
      orderByClassesEnabled: this.orderByClassesEnabled,
      valueExpression: this.valueExpression,
      valueExpressionTitle: this.valueExpressionTitle,
      fieldDelimiter: this.fieldDelimiter,
      visualVariables: i(this.visualVariables),
      legendOptions: i(this.legendOptions),
      authoringInfo: this.authoringInfo && this.authoringInfo.clone(),
      backgroundFillSymbol: i(this.backgroundFillSymbol)
    });
    this._isDefaultSymbolDerived && (e._isDefaultSymbolDerived = !0), e._set("portal", this.portal);
    const t = i(this.uniqueValueInfos),
      s = i(this.uniqueValueGroups);
    return this.styleOrigin && (e._set("styleOrigin", Object.freeze(i(this.styleOrigin))), Object.freeze(t), Object.freeze(s)), e._set("uniqueValueInfos", t), e._updateValueInfoMap(), e._set("uniqueValueGroups", s), e._isInfosSource = this._isInfosSource, e._watchUniqueValueInfosAndGroups(), e;
  }
  get arcadeRequired() {
    return this.arcadeRequiredForVisualVariables || !!this.valueExpression;
  }
  async collectRequiredFields(e, t) {
    const s = [this.collectVVRequiredFields(e, t), this.collectSymbolFields(e, t)];
    await Promise.all(s);
  }
  async collectSymbolFields(e, t) {
    const s = [...this.getSymbols().map(s => s.collectRequiredFields(e, t)), y(e, t, this.valueExpression)];
    b(e, t, this.field), b(e, t, this.field2), b(e, t, this.field3), await Promise.all(s);
  }
  populateFromStyle() {
    return D(this.styleOrigin, {
      portal: this.portal
    }).then(e => {
      const t = [];
      return this._valueInfoMap = {}, e && e.data && Array.isArray(e.data.items) && e.data.items.forEach(s => {
        const i = new x({
          styleUrl: e.styleUrl,
          styleName: e.styleName,
          portal: this.portal,
          name: s.name
        });
        this.defaultSymbol || s.name !== e.data.defaultItem || (this.defaultSymbol = i, this._isDefaultSymbolDerived = !0);
        const o = new j({
          value: s.name,
          symbol: i
        });
        t.push(o), this._valueInfoMap[s.name] = o;
      }), this._set("uniqueValueInfos", Object.freeze(t)), this._updateGroupsFromInfos(!0), this._isInfosSource = null, this._watchUniqueValueInfos(), !this.defaultSymbol && this.uniqueValueInfos?.length && (this.defaultSymbol = this.uniqueValueInfos[0].symbol, this._isDefaultSymbolDerived = !0), this;
    });
  }
  _updateFieldDelimiter() {
    this.field && this.field2 && !this.fieldDelimiter && this._set("fieldDelimiter", C);
  }
  _updateUniqueValues() {
    null != this._isInfosSource && (this._isInfosSource ? this._updateGroupsFromInfos() : this._updateInfosFromGroups());
  }
  _updateValueInfoMap() {
    this._valueInfoMap = {};
    const {
      uniqueValueInfos: e
    } = this;
    if (e) for (const t of e) this._valueInfoMap[t.value + ""] = t;
  }
  _watchUniqueValueInfosAndGroups() {
    this._watchUniqueValueInfos(), this._watchUniqueValueGroups();
  }
  _watchUniqueValueInfos() {
    this.removeHandles(R);
    const {
      uniqueValueInfos: e
    } = this;
    if (e) {
      const t = [];
      for (const s of e) t.push(r(() => ({
        symbol: s.symbol,
        value: s.value,
        label: s.label,
        description: s.description
      }), (e, t) => {
        e !== t && (this._updateGroupsFromInfos(), this._isInfosSource = !0);
      }, {
        sync: !0
      }));
      this.addHandles(t, R);
    }
  }
  _watchUniqueValueGroups() {
    this.removeHandles(P);
    const {
      uniqueValueGroups: e
    } = this;
    if (e) {
      const t = [];
      for (const s of e) {
        t.push(r(() => ({
          classes: s.classes
        }), (e, t) => {
          e !== t && (this._updateInfosFromGroups(), this._isInfosSource = !1);
        }, {
          sync: !0
        }));
        for (const e of s.classes ?? []) t.push(r(() => ({
          symbol: e.symbol,
          values: e.values,
          label: e.label,
          description: e.description
        }), (e, t) => {
          e !== t && (this._updateInfosFromGroups(), this._isInfosSource = !1);
        }, {
          sync: !0
        }));
      }
      this.addHandles(t, P);
    }
  }
  _updateInfosFromGroups() {
    if (!this.uniqueValueGroups) return this._set("uniqueValueInfos", null), this._updateValueInfoMap(), void this._watchUniqueValueInfos();
    const e = [],
      {
        field: t,
        field2: s,
        field3: i,
        fieldDelimiter: o,
        uniqueValueGroups: l,
        valueExpression: r
      } = this;
    if (!t && !r) return this._set("uniqueValueInfos", e), this._updateValueInfoMap(), void this._watchUniqueValueInfos();
    const u = !(!t || !s);
    for (const n of l) for (const t of n.classes ?? []) {
      const {
        symbol: l,
        label: r,
        values: n,
        description: a
      } = t;
      for (const t of n ?? []) {
        const {
            value: n,
            value2: p,
            value3: f
          } = t,
          d = [n];
        s && d.push(p), i && d.push(f);
        const c = u ? d.join(o || "") : d[0];
        e.push(new j({
          symbol: l,
          label: r,
          value: c,
          description: a
        }));
      }
    }
    this._set("uniqueValueInfos", e), this._updateValueInfoMap(), this._watchUniqueValueInfos();
  }
  _updateGroupsFromInfos(e = !1) {
    if (!this.uniqueValueInfos) return this._set("uniqueValueGroups", null), void this._watchUniqueValueGroups();
    const {
      field: t,
      field2: s,
      valueExpression: i,
      fieldDelimiter: o,
      uniqueValueInfos: l
    } = this;
    if (!t && !i || !l.length) return this._set("uniqueValueGroups", []), void this._watchUniqueValueGroups();
    const r = !(!t || !s),
      u = l.map(e => {
        const {
            symbol: t,
            label: s,
            value: i,
            description: l
          } = e,
          [u, n, a] = r ? i?.toString()?.split(o || "") || [] : [i];
        return new w({
          symbol: t,
          label: s,
          description: l,
          values: [new S({
            value: u,
            value2: n,
            value3: a
          })]
        });
      }),
      n = [new U({
        classes: u
      })];
    e && Object.freeze(n), this._set("uniqueValueGroups", n), this._watchUniqueValueGroups();
  }
  _getUniqueValueInfo(e, t) {
    return this.valueExpression ? this._getUnqiueValueInfoForExpression(e, t) : this._getUnqiueValueInfoForFields(e);
  }
  _getUnqiueValueInfoForExpression(e, t) {
    const {
      viewingMode: s,
      scale: i,
      spatialReference: o,
      arcade: l,
      timeZone: r
    } = t ?? {};
    let u = this._cache.compiledFunc;
    const n = l.arcadeUtils;
    if (!u) {
      const e = n.createSyntaxTree(this.valueExpression);
      u = n.createFunction(e), this._cache.compiledFunc = u;
    }
    const a = n.executeFunction(u, n.createExecContext(e, n.getViewInfo({
      viewingMode: s,
      scale: i,
      spatialReference: o
    }), r));
    return this._valueInfoMap[a + ""];
  }
  _getUnqiueValueInfoForFields(e) {
    const t = this.field,
      s = e.attributes;
    let i;
    if ("function" != typeof t && this.field2) {
      const e = this.field2,
        o = this.field3,
        l = [];
      t && l.push(s[t]), e && l.push(s[e]), o && l.push(s[o]), i = l.join(this.fieldDelimiter || "");
    } else "function" == typeof t ? i = t(e) : t && (i = s[t]);
    return this._valueInfoMap[i + ""];
  }
  static fromPortalStyle(e, t) {
    const s = new E(t?.properties);
    s._set("styleOrigin", Object.freeze({
      styleName: e
    })), s._set("portal", t?.portal || v.getDefault());
    const i = s.populateFromStyle();
    return i.catch(t => {
      N.error(`#fromPortalStyle('${e}'[, ...])`, "Failed to create unique value renderer from style name", t);
    }), i;
  }
  static fromStyleUrl(e, t) {
    const s = new E(t?.properties);
    s._set("styleOrigin", Object.freeze({
      styleUrl: e
    }));
    const i = s.populateFromStyle();
    return i.catch(t => {
      N.error(`#fromStyleUrl('${e}'[, ...])`, "Failed to create unique value renderer from style URL", t);
    }), i;
  }
};
e([u({
  readOnly: !0
})], k.prototype, "_cache", null), e([a({
  uniqueValue: "unique-value"
})], k.prototype, "type", void 0), e([u(g)], k.prototype, "backgroundFillSymbol", void 0), e([u({
  value: null,
  json: {
    type: String,
    read: {
      source: "field1"
    },
    write: {
      target: "field1"
    }
  }
})], k.prototype, "field", null), e([n("field")], k.prototype, "castField", null), e([d("field")], k.prototype, "writeField", null), e([u({
  type: String,
  value: null,
  json: {
    write: !0
  }
})], k.prototype, "field2", null), e([u({
  type: String,
  value: null,
  json: {
    write: !0
  }
})], k.prototype, "field3", null), e([u({
  type: Boolean,
  json: {
    name: "drawInClassOrder",
    default: !1,
    write: !0,
    origins: {
      "web-scene": {
        write: !1
      }
    }
  }
})], k.prototype, "orderByClassesEnabled", void 0), e([u({
  type: String,
  value: null,
  json: {
    write: !0
  }
})], k.prototype, "valueExpression", null), e([u({
  type: String,
  json: {
    write: !0
  }
})], k.prototype, "valueExpressionTitle", void 0), e([u({
  type: I,
  json: {
    write: !0
  }
})], k.prototype, "legendOptions", void 0), e([u({
  type: String,
  json: {
    write: !0
  }
})], k.prototype, "defaultLabel", void 0), e([u(l({
  ...q
}, {
  json: {
    write: {
      overridePolicy() {
        return {
          enabled: !this._isDefaultSymbolDerived
        };
      }
    },
    origins: {
      "web-scene": {
        write: {
          overridePolicy() {
            return {
              enabled: !this._isDefaultSymbolDerived
            };
          }
        }
      }
    }
  }
}))], k.prototype, "defaultSymbol", null), e([u({
  type: String,
  value: null,
  json: {
    write: !0
  }
})], k.prototype, "fieldDelimiter", null), e([u({
  type: v,
  readOnly: !0
})], k.prototype, "portal", void 0), e([p("portal", ["styleName"])], k.prototype, "readPortal", null), e([u({
  readOnly: !0,
  json: {
    write: {
      enabled: !1,
      overridePolicy: () => ({
        enabled: !0
      })
    }
  }
})], k.prototype, "styleOrigin", void 0), e([p("styleOrigin", ["styleName", "styleUrl"])], k.prototype, "readStyleOrigin", null), e([d("styleOrigin", {
  styleName: {
    type: String
  },
  styleUrl: {
    type: String
  }
})], k.prototype, "writeStyleOrigin", null), e([u({
  type: [U],
  json: {
    read: {
      source: ["uniqueValueGroups", "uniqueValueInfos"],
      reader: (e, t, s) => (t.uniqueValueGroups || z(t)).map(e => U.fromJSON(e, s))
    },
    write: {
      overridePolicy() {
        return this.styleOrigin ? {
          enabled: !1
        } : {
          enabled: !0
        };
      }
    }
  }
})], k.prototype, "uniqueValueGroups", null), e([u({
  type: [j],
  json: {
    read: !1,
    write: {
      overridePolicy() {
        return this.styleOrigin ? {
          enabled: !1
        } : {
          enabled: !0
        };
      }
    }
  }
})], k.prototype, "uniqueValueInfos", null), k = E = e([f(M)], k);
const A = k;
export { A as default };